import { type FC, type MouseEvent, type ReactNode } from "react";
import { Tooltip } from "@onshopfront/components";
import { usePermissions } from "../../../../Utilities/Domain/Users/Permissions";
import FontAwesomeLight from "../../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import FontAwesomeSolid from "../../../../Utilities/Platform/FontAwesome/FontAwesomeSolid";
import Button from "../../../Buttons/Button";
import {
    getFile,
    type MediaDirectoryType,
    type MediaFilePages,
    MediaItemType,
    type MediaSelectedType,
} from "../MediaUtilities";
import { FileDetails } from "./FileDetails";
import { FolderDetails } from "./FolderDetails";
import { MultiDetails } from "./MultiDetails";

interface Props {
    small?: boolean;
    selected: Array<MediaSelectedType>;
    pages: MediaFilePages;
    actions: boolean;
    directory: MediaDirectoryType | null;
    onClick: (e: MouseEvent) => void;
    onRename: (e: MouseEvent) => void;
    onDelete: (e: MouseEvent) => void;
    disableSelect: boolean;
    multi: boolean;
}

// Used to determine which version of unit divisions is used (1024 for binary divisions and 1000 for SI units)
const UNIT_DIVISIONS = 1000;

// FROM: https://stackoverflow.com/a/20732091
export function getSizeString(size: number): string {
    const i = size ? Math.floor(Math.log(size) / Math.log(UNIT_DIVISIONS)) : 0;

    return Math.round((size / Math.pow(UNIT_DIVISIONS, i)) * 100) / 100 + " " + [ "B", "kB", "MB", "GB", "TB" ][i];
}

const permissions = [
    "delete_media_content",
    "rename_media_content",
    "rename_media_folder",
    "delete_media_folder",
] as const;

export const MediaDetails: FC<Props> = ({
    small,
    selected,
    pages,
    directory,
    actions,
    onClick,
    onRename,
    onDelete,
    disableSelect,
    multi,
}) => {
    const {
        renameMediaContent,
        deleteMediaContent,
        deleteMediaFolder,
        renameMediaFolder,
    } = usePermissions(permissions);

    const showActions = actions && !small;

    let select = false;
    let canRename = false;
    let canDelete = false;
    let deleteDisabledReason = "";

    let content: ReactNode = null;

    if(selected.length === 1 && directory) {
        if(selected[0].type === MediaItemType.File) {
            const file = getFile(selected[0].index, pages);

            if(file) {
                canDelete = deleteMediaContent && file.connections.length === 0;
                canRename = renameMediaContent;
                select = true;

                if(!deleteMediaContent) {
                    deleteDisabledReason = "You do not have permission to delete this file";
                } else if(file.connections.length !== 0) {
                    deleteDisabledReason = "Cannot delete a file that is being used somewhere in Shopfront";
                }

                content = (
                    <FileDetails
                        file={file}
                        small={small}
                    />
                );
            } else {
                content = (
                    <div className="item not-selected">
                        <div className="media">
                            <FontAwesomeLight icon="file-image" />
                        </div>
                        <div className="details">
                            <span className="name">Select a file</span>
                        </div>
                    </div>
                );
            }
        } else if(selected[0].type === MediaItemType.Folder) {
            canDelete = deleteMediaFolder;
            canRename = renameMediaFolder;

            if(!deleteMediaFolder) {
                deleteDisabledReason = "You do not have permission to delete this folder";
            }

            content = (
                <FolderDetails
                    folder={directory.folders[selected[0].index]}
                    small={small}
                />
            );
        }
    } else if(selected.length > 1) {
        // Gets the first file that isn't in use somewhere in the pos
        const hasUnattached = typeof selected.find(selectedItem => {
            const file = getFile(selectedItem.index, pages);

            if(file) {
                return file.connections.length === 0;
            }

            return false;
        }) !== "undefined";

        canDelete = deleteMediaContent && hasUnattached;

        if(!deleteMediaContent) {
            deleteDisabledReason = "You do not have permission to delete these files";
        } else if(!hasUnattached) {
            deleteDisabledReason = "All of the files selected are being used somewhere in Shopfront";
        }

        select = multi;

        content = (
            <MultiDetails
                selected={selected}
                pages={pages}
            />
        );
    } else {
        content = (
            <div className="item not-selected">
                <div className="media">
                    <FontAwesomeLight icon="file-image" />
                </div>
                <div className="details">
                    <span className="name">Select a file</span>
                </div>
            </div>
        );
    }

    let selectButton = null;

    if(!disableSelect) {
        selectButton = (
            <div className={"select-button"}>
                <Button
                    onClick={onClick}
                    disabled={!select}
                >
                    Select
                </Button>
            </div>
        );
    }

    const disableRename = !(showActions && canRename);
    const disableDelete = !(showActions && canDelete);

    return (
        <div className="file-details">
            {!small && (
                <div className="actions">
                    <Tooltip
                        location="bottom"
                        title={
                            selected.length > 1 ? (
                                "You cannot rename multiple files at once"
                            ) : (
                                "You do not have permission to rename " + (
                                    selected[0]?.type === MediaItemType.Folder ?
                                        "this folder" :
                                        "this file"
                                )
                            )
                        }
                        disabled={!disableRename || !selected.length}
                    >
                        {(ref, getProps) => (
                            <div
                                ref={ref}
                                {...getProps()}
                            >
                                <Button
                                    classes={[ "file-details-action rename" ]}
                                    onClick={onRename}
                                    disabled={disableRename}
                                >
                                    <FontAwesomeSolid icon="edit" />Rename
                                </Button>
                            </div>
                        )}
                    </Tooltip>
                    <Tooltip
                        location="bottom"
                        title={deleteDisabledReason}
                        disabled={!disableDelete || !disableDelete || !selected.length}
                    >
                        {(ref, getProps) => (
                            <div
                                ref={ref}
                                {...getProps()}
                            >
                                <Button
                                    classes={[ "file-details-action delete" ]}
                                    onClick={onDelete}
                                    disabled={disableDelete}
                                >
                                    <FontAwesomeSolid icon="trash-alt" />Delete
                                </Button>
                            </div>
                        )}
                    </Tooltip>
                </div>
            )}
            {content}
            {selectButton}
        </div>
    );
};
